// doctor-frontend/src/pages/admin/ActivitiesManager.jsx
// NO-REGRESSION update:
// - Preserves existing /api/activity-templates template create/edit/toggle flow.
// - Adds tenant activity-config loading/saving for pilot Core Practice Area/catalogue fields.
// - Keeps ActivityTemplate as guided-run execution template layer.
// - Uses /api/admin/activity-config as tenant pilot-control layer when available.
// - Reduces dense table columns; detailed Core Practice Area/skill fields remain in dialog.
// - Activity config failure is non-blocking so old template manager still works.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';

import { getJSON, postJSON, putJSON, patchJSON } from '@/services/http';

const ACTIVITY_TEMPLATES_URL = '/api/activity-templates';
const ACTIVITY_CONFIG_URL = '/api/admin/activity-config';
const ACTIVITY_CONFIG_CATALOGUE_URL = '/api/admin/activity-config/catalogue';

const CATEGORIES = ['', 'Core Practice Area', 'Clinical', 'Academic', 'Community', 'Mandatory', 'Programme', 'Management', 'Counselling'];
const CAPTURE_MODES = ['Embedded', 'Independent', 'Dual-mode'];
const SCHEDULE_FITS = [
  'Morning_OPD',
  'Morning_Parallel',
  'Afternoon',
  'Anytime',
  'Event',
  'Morning_OPD|Morning_Parallel',
  'Morning_OPD|Morning_Parallel|Anytime',
  'Morning_Parallel|Anytime',
  'Morning_Parallel|Anytime|Event',
  'Morning_Parallel|Afternoon|Anytime',
  'Afternoon|Anytime',
];
const MODE_ALLOWED = ['Observed', 'Performed', 'Both'];
const FINAL_MODES = ['embedded_only', 'independent_only', 'dual_mode'];
const PILOT_STATUS = ['keep', 'merge', 'hold'];
const FINAL_GROUPS = ['Core Practice Area', 'Clinical', 'Counselling', 'Programme', 'Community', 'Management', 'Academic'];

const DEFAULT_ROLES = ['Intern', 'Junior Resident', 'Senior Resident', 'Faculty'];
const DEFAULT_CENTRES = ['Keru', 'Dhawa', 'Dhundhara', 'UHTC'];

const toList = (value) => {
  if (Array.isArray(value)) return value.map((item) => String(item || '').trim()).filter(Boolean);
  if (typeof value !== 'string') return [];
  return value
    .split(/[|,]/g)
    .map((item) => item.trim())
    .filter(Boolean);
};

const toDelimited = (value) => toList(value).join('|');

const slugifyKey = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const displayMode = (value) => {
  const map = {
    embedded_only: 'Embedded',
    independent_only: 'Independent',
    dual_mode: 'Dual',
  };
  return map[value] || value || '-';
};

const displayStatus = (value) => {
  const map = {
    keep: 'Keep',
    merge: 'Merge',
    hold: 'Hold',
  };
  return map[value] || value || '-';
};

const CORE_PRACTICE_AREA_LABEL = 'Core Practice Area';

const formatDisplayText = (value) => {
  const input = value == null ? '' : String(value);
  if (!input) return '';
  return input
    .replace(/Entrustable\s+Professional\s+Activities\s*\(\s*EPAs\s*\)/gi, `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(/Entrustable\s+Professional\s+Activities/gi, `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(/\bEPAs\b/g, `${CORE_PRACTICE_AREA_LABEL}s`)
    .replace(/\bEPA\b/g, CORE_PRACTICE_AREA_LABEL);
};

const displayText = (value, fallback = '-') => {
  const out = formatDisplayText(value);
  return out || fallback;
};

const pickPilotStatusColor = (value) => {
  if (value === 'keep') return 'success';
  if (value === 'merge') return 'warning';
  if (value === 'hold') return 'default';
  return 'default';
};

const pickFinalModeColor = (value) => {
  if (value === 'dual_mode') return 'secondary';
  if (value === 'embedded_only') return 'info';
  if (value === 'independent_only') return 'primary';
  return 'default';
};

const emptyForm = () => ({
  slug: '',
  title: '',
  category: 'Clinical',
  goals: [''],
  steps: [{ order: 1, title: '' }],
  observerRole: 'sr',
  rubricHints: [''],
  standardsRefs: [{ label: '' }],
  active: true,

  key: '',
  epaTitle: '',
  competencies: '',
  skills: '',
  captureMode: 'Independent',
  scheduleFit: 'Anytime',
  allowedRoles: [...DEFAULT_ROLES],
  allowedCentres: [...DEFAULT_CENTRES],
  modeAllowed: 'Both',
  requiresReviewer: true,
  isPilotVisible: true,
  displayOrder: 0,
  notes: '',
  finalMode: 'independent_only',
  pilotStatus: 'keep',
  finalGroup: 'Clinical',
});

function normalizeCatalogueItem(item = {}, idx = 0) {
  const title = item.corePracticeAreaTitle || item.practiceAreaTitle || item.epaTitle || item.title || '';
  const key = item.key || item.slug || slugifyKey(title || `${item.category || 'activity'}-${idx + 1}`);

  return {
    key: String(key || '').trim(),
    epaTitle: item.corePracticeAreaTitle || item.practiceAreaTitle || item.epaTitle || item.title || '',
    category: item.category || '',
    competencies: toList(item.competencies),
    skills: toList(item.skills),
    captureMode: item.captureMode || '',
    scheduleFit: toList(item.scheduleFit),
    allowedRoles: toList(item.allowedRoles),
    allowedCentres: toList(item.allowedCentres),
    modeAllowed: item.modeAllowed || '',
    requiresReviewer: item.requiresReviewer !== false,
    isPilotVisible: item.isPilotVisible !== false,
    displayOrder: Number.isFinite(Number(item.displayOrder)) ? Number(item.displayOrder) : idx + 1,
    notes: item.notes || '',
    finalMode: item.finalMode || '',
    pilotStatus: item.pilotStatus || '',
    finalGroup: item.finalGroup || '',
    active: item.active !== false,
  };
}

function normalizeTemplate(t = {}, catalogueItem = null, idx = 0) {
  const config = catalogueItem ? normalizeCatalogueItem(catalogueItem, idx) : null;
  const key = config?.key || t.slug || slugifyKey(t.title || t._id || `activity-${idx + 1}`);

  return {
    _id: t._id || t.id || '',
    _configOnly: !t._id && !t.id,
    key,
    slug: t.slug || config?.key || '',
    title: t.title || config?.epaTitle || '',
    category: t.category || config?.category || '',
    active: t.active !== false && config?.active !== false,
    goals: Array.isArray(t.goals) ? t.goals : [],
    steps: Array.isArray(t.steps) ? t.steps : [],
    observerRole: t.observerRole || 'sr',
    rubricHints: Array.isArray(t.rubricHints) ? t.rubricHints : [],
    standardsRefs: Array.isArray(t.standardsRefs) ? t.standardsRefs : [],

    epaTitle: config?.epaTitle || t.epaTitle || t.title || '',
    competencies: toDelimited(config?.competencies || t.competencies || ''),
    skills: toDelimited(config?.skills || t.skills || ''),
    captureMode: config?.captureMode || t.captureMode || 'Independent',
    scheduleFit: toDelimited(config?.scheduleFit || t.scheduleFit || 'Anytime') || 'Anytime',
    allowedRoles: toList(config?.allowedRoles || t.allowedRoles || DEFAULT_ROLES),
    allowedCentres: toList(config?.allowedCentres || t.allowedCentres || DEFAULT_CENTRES),
    modeAllowed: config?.modeAllowed || t.modeAllowed || 'Both',
    requiresReviewer: config?.requiresReviewer ?? t.requiresReviewer ?? true,
    isPilotVisible: config?.isPilotVisible ?? t.isPilotVisible ?? true,
    displayOrder: Number.isFinite(Number(config?.displayOrder ?? t.displayOrder))
      ? Number(config?.displayOrder ?? t.displayOrder)
      : idx + 1,
    notes: config?.notes || t.notes || '',
    finalMode: config?.finalMode || t.finalMode || 'independent_only',
    pilotStatus: config?.pilotStatus || t.pilotStatus || 'keep',
    finalGroup: config?.finalGroup || t.finalGroup || t.category || 'Clinical',
  };
}

function buildTemplatePayload(form) {
  return {
    slug: String(form.slug || '').trim().toLowerCase(),
    title: String(form.title || form.epaTitle || '').trim(),
    category: String(form.category || form.finalGroup || '').trim() || 'Clinical',
    observerRole: String(form.observerRole || 'sr').trim().toLowerCase(),
    goals: (Array.isArray(form.goals) ? form.goals : [])
      .map((v) => String(v || '').trim())
      .filter(Boolean),
    steps: (Array.isArray(form.steps) ? form.steps : [])
      .map((step, idx) => ({
        order: Number(step?.order) || idx + 1,
        title: String(step?.title || '').trim(),
      }))
      .filter((step) => step.title),
    rubricHints: (Array.isArray(form.rubricHints) ? form.rubricHints : [])
      .map((v) => String(v || '').trim())
      .filter(Boolean),
    standardsRefs: (Array.isArray(form.standardsRefs) ? form.standardsRefs : [])
      .map((ref) => ({ label: String(ref?.label || '').trim() }))
      .filter((ref) => ref.label),
    active: !!form.active,
  };
}

function buildCatalogueItem(form, idx = 0) {
  const title = String(form.epaTitle || form.title || '').trim();
  const key = String(form.key || form.slug || slugifyKey(title)).trim();

  return {
    key,
    epaTitle: title,
    category: String(form.category || form.finalGroup || '').trim() || 'Clinical',
    competencies: toList(form.competencies),
    skills: toList(form.skills),
    captureMode: String(form.captureMode || 'Independent').trim(),
    scheduleFit: toList(form.scheduleFit || 'Anytime'),
    allowedRoles: toList(form.allowedRoles),
    allowedCentres: toList(form.allowedCentres),
    modeAllowed: String(form.modeAllowed || 'Both').trim(),
    requiresReviewer: !!form.requiresReviewer,
    isPilotVisible: !!form.isPilotVisible,
    displayOrder: Number(form.displayOrder) || idx + 1,
    notes: String(form.notes || '').trim(),
    finalMode: String(form.finalMode || 'independent_only').trim(),
    pilotStatus: String(form.pilotStatus || 'keep').trim(),
    finalGroup: String(form.finalGroup || form.category || 'Clinical').trim(),
    active: !!form.active,
  };
}

function mergeTemplateAndConfig(templateItems = [], configItems = []) {
  const configMap = new Map();
  const normalizedConfig = configItems.map((item, idx) => normalizeCatalogueItem(item, idx));

  normalizedConfig.forEach((item) => {
    const keys = [item.key, slugifyKey(item.epaTitle), slugifyKey(item.category ? `${item.category}-${item.epaTitle}` : '')]
      .map((x) => String(x || '').trim())
      .filter(Boolean);
    keys.forEach((k) => {
      if (!configMap.has(k)) configMap.set(k, item);
    });
  });

  const used = new Set();
  const merged = templateItems.map((template, idx) => {
    const candidates = [template.slug, slugifyKey(template.title), template._id]
      .map((x) => String(x || '').trim())
      .filter(Boolean);
    const config = candidates.map((k) => configMap.get(k)).find(Boolean) || null;
    if (config?.key) used.add(config.key);
    return normalizeTemplate(template, config, idx);
  });

  normalizedConfig
    .filter((item) => item.key && !used.has(item.key))
    .forEach((item, idx) => {
      merged.push(normalizeTemplate({}, item, templateItems.length + idx));
    });

  return merged.sort((a, b) => {
    const ao = Number(a.displayOrder) || 9999;
    const bo = Number(b.displayOrder) || 9999;
    if (ao !== bo) return ao - bo;
    return String(a.title || a.epaTitle).localeCompare(String(b.title || b.epaTitle));
  });
}

export default function ActivitiesManager() {
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [rows, setRows] = useState([]);
  const [templateRows, setTemplateRows] = useState([]);
  const [catalogueRows, setCatalogueRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [configWarning, setConfigWarning] = useState('');

  const [pilotMode, setPilotMode] = useState(true);
  const [legacyMode, setLegacyMode] = useState(false);
  const [legacyCutoffDate, setLegacyCutoffDate] = useState('');
  const [configLoaded, setConfigLoaded] = useState(false);

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState('');
  const [form, setForm] = useState(emptyForm());

  async function load(overrides = {}) {
    const qVal = overrides.q ?? q;
    const categoryVal = overrides.category ?? category;

    setErr('');
    setOk('');
    setConfigWarning('');
    setLoading(true);

    try {
      const params = {};
      if (qVal) params.q = qVal;
      if (categoryVal) params.category = categoryVal;

      const templateRes = await getJSON(ACTIVITY_TEMPLATES_URL, { params });
      const templates = Array.isArray(templateRes?.items)
        ? templateRes.items
        : (Array.isArray(templateRes) ? templateRes : []);

      let catalogue = catalogueRows;
      try {
        const configRes = await getJSON(ACTIVITY_CONFIG_URL);
        const config = configRes?.config || {};
        catalogue = Array.isArray(config.catalogue) ? config.catalogue : [];
        setPilotMode(config.pilotMode !== false);
        setLegacyMode(!!config.legacyMode);
        setLegacyCutoffDate(config.legacyCutoffDate ? String(config.legacyCutoffDate).slice(0, 10) : '');
        setConfigLoaded(true);
        setCatalogueRows(catalogue);
      } catch (configErr) {
        setConfigLoaded(false);
        setConfigWarning(
          configErr?.message
            ? `Pilot config not loaded: ${configErr.message}. Template library is still usable.`
            : 'Pilot config not loaded. Template library is still usable.'
        );
      }

      setTemplateRows(templates);
      setRows(mergeTemplateAndConfig(templates, catalogue));
    } catch (e) {
      setErr(e.message || 'Failed to load activity templates');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredRows = useMemo(() => {
    const search = q.trim().toLowerCase();
    return rows.filter((row) => {
      const matchesCategory = !category || row.category === category || row.finalGroup === category;
      if (!matchesCategory) return false;
      if (!search) return true;
      const haystack = [
        row.title,
        row.epaTitle,
        row.slug,
        row.key,
        row.category,
        row.finalGroup,
        row.finalMode,
        row.pilotStatus,
        row.captureMode,
        row.scheduleFit,
        row.competencies,
        row.skills,
        row.notes,
        ...(row.allowedRoles || []),
        ...(row.allowedCentres || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(search);
    });
  }, [rows, q, category]);

  const stats = useMemo(() => {
    const total = rows.length;
    const visible = rows.filter((r) => r.isPilotVisible !== false).length;
    const kept = rows.filter((r) => r.pilotStatus === 'keep').length;
    const held = rows.filter((r) => r.pilotStatus === 'hold').length;
    const dual = rows.filter((r) => r.finalMode === 'dual_mode').length;
    return { total, visible, kept, held, dual };
  }, [rows]);

  const onSearch = () => load();
  const onReset = () => {
    setQ('');
    setCategory('');
    load({ q: '', category: '' });
  };

  const openAdd = () => {
    setEditId('');
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditId(t._id || '');
    setForm({
      ...emptyForm(),
      ...normalizeTemplate(t, null, 0),
      goals: Array.isArray(t.goals) && t.goals.length ? t.goals : [''],
      steps: Array.isArray(t.steps) && t.steps.length ? t.steps : [{ order: 1, title: '' }],
      rubricHints: Array.isArray(t.rubricHints) && t.rubricHints.length ? t.rubricHints : [''],
      standardsRefs: Array.isArray(t.standardsRefs) && t.standardsRefs.length ? t.standardsRefs : [{ label: '' }],
    });
    setOpen(true);
  };

  const rebuildCatalogueWith = (nextItem) => {
    const nextKey = nextItem.key || slugifyKey(nextItem.epaTitle || nextItem.title);
    const byKey = new Map();

    rows.forEach((row, idx) => {
      const item = buildCatalogueItem(row, idx);
      if (item.key) byKey.set(item.key, item);
    });

    if (nextKey) byKey.set(nextKey, nextItem);

    return Array.from(byKey.values())
      .filter((item) => item.key && item.epaTitle)
      .sort((a, b) => (Number(a.displayOrder) || 9999) - (Number(b.displayOrder) || 9999));
  };

  const saveCatalogue = async (items, successMessage = 'Pilot catalogue updated.') => {
    setSavingConfig(true);
    try {
      const res = await patchJSON(ACTIVITY_CONFIG_CATALOGUE_URL, { items });
      const catalogue = Array.isArray(res?.catalogue) ? res.catalogue : items;
      setCatalogueRows(catalogue);
      setRows(mergeTemplateAndConfig(templateRows, catalogue));
      setConfigLoaded(true);
      setOk(successMessage);
    } catch (e) {
      setConfigWarning(
        e?.message
          ? `Template saved, but pilot catalogue was not updated: ${e.message}`
          : 'Template saved, but pilot catalogue was not updated.'
      );
    } finally {
      setSavingConfig(false);
    }
  };

  const saveConfig = async () => {
    setErr('');
    setOk('');
    setSavingConfig(true);
    try {
      const items = rows.map((row, idx) => buildCatalogueItem(row, idx));
      const res = await putJSON(ACTIVITY_CONFIG_URL, {
        pilotMode,
        legacyMode,
        legacyCutoffDate: legacyCutoffDate || null,
        catalogue: items,
      });

      const config = res?.config || {};
      const catalogue = Array.isArray(config.catalogue) ? config.catalogue : items;
      setCatalogueRows(catalogue);
      setRows(mergeTemplateAndConfig(templateRows, catalogue));
      setConfigLoaded(true);
      setOk('Pilot activity configuration saved.');
    } catch (e) {
      setErr(e.message || 'Failed to save pilot activity configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  const save = async () => {
    setErr('');
    setOk('');
    setConfigWarning('');
    try {
      const payload = buildTemplatePayload(form);
      const catalogueItem = buildCatalogueItem(form, rows.length);

      let savedTemplate = null;
      if (editId) {
        const res = await patchJSON(`${ACTIVITY_TEMPLATES_URL}/${editId}`, payload);
        savedTemplate = res?.item || null;
        setOk('Template updated.');
      } else {
        const res = await postJSON(ACTIVITY_TEMPLATES_URL, payload);
        savedTemplate = res?.item || null;
        setOk('Template created.');
      }

      setOpen(false);

      const nextTemplateRows = savedTemplate
        ? [
            ...templateRows.filter((row) => String(row._id || row.id) !== String(savedTemplate._id || savedTemplate.id)),
            savedTemplate,
          ]
        : templateRows;

      setTemplateRows(nextTemplateRows);
      const catalogueWithSaved = rebuildCatalogueWith({
        ...catalogueItem,
        key: savedTemplate?.slug || catalogueItem.key,
        epaTitle: catalogueItem.epaTitle || savedTemplate?.title || '',
        category: catalogueItem.category || savedTemplate?.category || 'Clinical',
        active: savedTemplate?.active !== false,
      });

      await saveCatalogue(catalogueWithSaved, 'Template and pilot catalogue updated.');
      await load();
      try { window.dispatchEvent(new CustomEvent('activities:updated')); } catch {}
    } catch (e) {
      setErr(e.message || 'Failed to save template');
    }
  };

  const toggle = async (t) => {
    setErr('');
    setOk('');
    try {
      if (t._id) {
        await patchJSON(`${ACTIVITY_TEMPLATES_URL}/${t._id}`, { active: !t.active });
      }

      const updatedRows = rows.map((row) =>
        row.key === t.key ? { ...row, active: !t.active } : row
      );
      setRows(updatedRows);

      const items = updatedRows.map((row, idx) => buildCatalogueItem(row, idx));
      await saveCatalogue(items, 'Activity status updated.');
      await load();
    } catch (e) {
      setErr(e.message || 'Failed to toggle activity');
    }
  };

  const togglePilotVisible = async (t) => {
    setErr('');
    setOk('');
    try {
      const updatedRows = rows.map((row) =>
        row.key === t.key ? { ...row, isPilotVisible: !row.isPilotVisible } : row
      );
      setRows(updatedRows);
      const items = updatedRows.map((row, idx) => buildCatalogueItem(row, idx));
      await saveCatalogue(items, 'Pilot visibility updated.');
    } catch (e) {
      setErr(e.message || 'Failed to update pilot visibility');
    }
  };

  const setGoal = (i, v) => setForm((f) => ({ ...f, goals: f.goals.map((g, idx) => idx === i ? v : g) }));
  const addGoal = () => setForm((f) => ({ ...f, goals: [...f.goals, ''] }));
  const setStep = (i, key, v) => setForm((f) => ({
    ...f,
    steps: f.steps.map((s, idx) => idx === i ? { ...s, [key]: v } : s),
  }));
  const addStep = () => setForm((f) => ({ ...f, steps: [...f.steps, { order: f.steps.length + 1, title: '' }] }));
  const setRef = (i, v) => setForm((f) => ({ ...f, standardsRefs: f.standardsRefs.map((r, idx) => idx === i ? { label: v } : r) }));
  const addRef = () => setForm((f) => ({ ...f, standardsRefs: [...f.standardsRefs, { label: '' }] }));
  const setHint = (i, v) => setForm((f) => ({ ...f, rubricHints: f.rubricHints.map((r, idx) => idx === i ? v : r) }));
  const addHint = () => setForm((f) => ({ ...f, rubricHints: [...f.rubricHints, ''] }));

  return (
    <Box className="p-4 md:p-6">
      <Typography variant="h5" className="font-semibold mb-2">Activities, Core Practice Areas & Competencies</Typography>
      <Typography variant="body2" color="text.secondary" className="mb-3">
        Manage guided activity templates and tenant-level Core Practice Area / competency / skill pilot controls without mixing the two data layers.
      </Typography>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 2 }}>{ok}</Alert>}
      {configWarning && <Alert severity="warning" sx={{ mb: 2 }}>{configWarning}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Pilot activity control
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Template execution still uses activity-templates. Pilot visibility, Core Practice Area grouping, mode and legacy settings save to admin activity-config.
            </Typography>
          </Box>
          <Chip
            size="small"
            color={configLoaded ? 'success' : 'warning'}
            label={configLoaded ? 'Config connected' : 'Template-only mode'}
          />
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }} alignItems={{ xs: 'stretch', md: 'center' }}>
          <FormControlLabel
            control={<Switch checked={!!pilotMode} onChange={(e) => setPilotMode(e.target.checked)} />}
            label="Pilot Mode"
          />
          <FormControlLabel
            control={<Switch checked={!!legacyMode} onChange={(e) => setLegacyMode(e.target.checked)} />}
            label="Legacy Mode"
          />
          <TextField
            label="Legacy Cutoff Date"
            type="date"
            value={legacyCutoffDate}
            onChange={(e) => setLegacyCutoffDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 190 }}
          />
          <Button
            startIcon={<SaveIcon />}
            variant="contained"
            onClick={saveConfig}
            disabled={savingConfig || loading}
          >
            Save Config
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Chip label={`${stats.total} total`} />
          <Chip color="success" label={`${stats.visible} pilot visible`} />
          <Chip color="primary" label={`${stats.kept} keep`} />
          <Chip color="warning" label={`${stats.held} hold`} />
          <Chip color="secondary" label={`${stats.dual} dual-mode`} />
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Search activity / Core Practice Area / group / mode / skill"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            fullWidth
          />
          <TextField
            select
            label="Category / Group"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            sx={{ minWidth: 210 }}
          >
            {CATEGORIES.map((c) => <MenuItem key={c || 'any'} value={c}>{displayText(c || 'Any')}</MenuItem>)}
          </TextField>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={onSearch}>Reload from server</Button>
          <Button variant="outlined" onClick={onReset}>Reset</Button>
          <Button startIcon={<AddIcon />} variant="outlined" onClick={openAdd}>Add Template</Button>
          <Button startIcon={<RefreshIcon />} variant="text" onClick={() => load()} disabled={loading}>Refresh</Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {filteredRows.length ? `${filteredRows.length} activities / templates` : 'No activities found'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Compact pilot grid; detailed competencies, skills, schedule and guided steps are inside Edit.
          </Typography>
        </Stack>

        {filteredRows.length === 0 ? (
          <Alert severity="info">Nothing to show. Try widening filters or add a template.</Alert>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 980 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 260 }}>Activity / Core Practice Area</TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell>Mode</TableCell>
                  <TableCell>Pilot</TableCell>
                  <TableCell>Visible</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Reviewer</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((t) => (
                  <TableRow key={t.key || t._id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {displayText(t.title || t.epaTitle)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {displayText(t.slug || t.key)}
                      </Typography>
                      {t._configOnly && (
                        <Chip size="small" sx={{ ml: 1 }} label="Config only" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{displayText(t.finalGroup || t.category)}</Typography>
                      <Typography variant="caption" color="text.secondary">{displayText(t.category)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={pickFinalModeColor(t.finalMode)}
                        label={displayMode(t.finalMode)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={pickPilotStatusColor(t.pilotStatus)}
                        label={displayStatus(t.pilotStatus)}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={<Switch size="small" checked={!!t.isPilotVisible} onChange={() => togglePilotVisible(t)} />}
                        label={t.isPilotVisible ? 'Yes' : 'No'}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={<Switch size="small" checked={!!t.active} onChange={() => toggle(t)} />}
                        label={t.active ? 'On' : 'Off'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{t.requiresReviewer ? 'Required' : 'Not required'}</Typography>
                      <Typography variant="caption" color="text.secondary">{t.observerRole || '-'}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit template and Core Practice Area fields">
                        <IconButton size="small" onClick={() => openEdit(t)}>
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editId ? 'Edit Activity / Core Practice Area' : 'Add Activity / Core Practice Area'}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Template identity</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Slug"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value.trim().toLowerCase(), key: e.target.value.trim().toLowerCase() })}
                helperText="Used by guided activity templates"
                fullWidth
              />
              <TextField
                label="Title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value, epaTitle: form.epaTitle || e.target.value })}
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField select label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} fullWidth>
                {CATEGORIES.filter(Boolean).map((c) => <MenuItem key={c} value={c}>{displayText(c)}</MenuItem>)}
              </TextField>
              <TextField select label="Observer Role" value={form.observerRole} onChange={(e) => setForm({ ...form, observerRole: e.target.value })} fullWidth>
                {['jr', 'sr', 'faculty', 'any'].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
              <TextField label="Display Order" type="number" value={form.displayOrder} onChange={(e) => setForm({ ...form, displayOrder: Number(e.target.value) })} sx={{ minWidth: 140 }} />
              <FormControlLabel
                control={<Switch checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />}
                label="Active"
              />
            </Stack>

            <Divider />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Pilot Core Practice Area / competency control</Typography>

            <TextField
              label="Core Practice Area Title"
              value={form.epaTitle}
              onChange={(e) => setForm({ ...form, epaTitle: e.target.value })}
              helperText="Falls back to template title if left blank"
              fullWidth
            />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField select label="Capture Mode" value={form.captureMode} onChange={(e) => setForm({ ...form, captureMode: e.target.value })} fullWidth>
                {CAPTURE_MODES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
              <TextField select label="Schedule Fit" value={form.scheduleFit} onChange={(e) => setForm({ ...form, scheduleFit: e.target.value })} fullWidth>
                {SCHEDULE_FITS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
              <TextField select label="Mode Allowed" value={form.modeAllowed} onChange={(e) => setForm({ ...form, modeAllowed: e.target.value })} fullWidth>
                {MODE_ALLOWED.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField select label="Final Mode" value={form.finalMode} onChange={(e) => setForm({ ...form, finalMode: e.target.value })} fullWidth>
                {FINAL_MODES.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
              <TextField select label="Pilot Status" value={form.pilotStatus} onChange={(e) => setForm({ ...form, pilotStatus: e.target.value })} fullWidth>
                {PILOT_STATUS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
              <TextField select label="Final Group" value={form.finalGroup} onChange={(e) => setForm({ ...form, finalGroup: e.target.value })} fullWidth>
                {FINAL_GROUPS.map((v) => <MenuItem key={v} value={v}>{displayText(v)}</MenuItem>)}
              </TextField>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Allowed Roles"
                value={toDelimited(form.allowedRoles)}
                onChange={(e) => setForm({ ...form, allowedRoles: toList(e.target.value) })}
                helperText="Use pipe-separated values"
                fullWidth
              />
              <TextField
                label="Allowed Centres"
                value={toDelimited(form.allowedCentres)}
                onChange={(e) => setForm({ ...form, allowedCentres: toList(e.target.value) })}
                helperText="Use pipe-separated values"
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControlLabel
                control={<Switch checked={!!form.requiresReviewer} onChange={(e) => setForm({ ...form, requiresReviewer: e.target.checked })} />}
                label="Requires Reviewer"
              />
              <FormControlLabel
                control={<Switch checked={!!form.isPilotVisible} onChange={(e) => setForm({ ...form, isPilotVisible: e.target.checked })} />}
                label="Pilot Visible"
              />
            </Stack>

            <TextField
              label="Competencies"
              value={form.competencies}
              onChange={(e) => setForm({ ...form, competencies: e.target.value })}
              helperText="Use pipe or comma-separated values"
              multiline
              minRows={2}
              fullWidth
            />

            <TextField
              label="Skills"
              value={form.skills}
              onChange={(e) => setForm({ ...form, skills: e.target.value })}
              helperText="Use pipe or comma-separated values"
              multiline
              minRows={2}
              fullWidth
            />

            <TextField
              label="Notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              multiline
              minRows={2}
              fullWidth
            />

            <Divider />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Guided activity template details</Typography>

            <Typography variant="subtitle2">Goals</Typography>
            {form.goals.map((g, i) => (
              <TextField key={`g-${i}`} value={g} onChange={(e) => setGoal(i, e.target.value)} fullWidth sx={{ mb: 1 }} />
            ))}
            <Button size="small" onClick={addGoal}>+ Add Goal</Button>

            <Typography variant="subtitle2" sx={{ mt: 2 }}>Steps</Typography>
            {form.steps.map((s, i) => (
              <Stack key={`s-${i}`} direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 1 }}>
                <TextField label="Order" type="number" value={s.order || i + 1} onChange={(e) => setStep(i, 'order', Number(e.target.value))} />
                <TextField label="Title" value={s.title || ''} onChange={(e) => setStep(i, 'title', e.target.value)} fullWidth />
              </Stack>
            ))}
            <Button size="small" onClick={addStep}>+ Add Step</Button>

            <Typography variant="subtitle2" sx={{ mt: 2 }}>Standards References</Typography>
            {form.standardsRefs.map((r, i) => (
              <TextField key={`r-${i}`} value={r.label || ''} onChange={(e) => setRef(i, e.target.value)} fullWidth sx={{ mb: 1 }} />
            ))}
            <Button size="small" onClick={addRef}>+ Add Reference</Button>

            <Typography variant="subtitle2" sx={{ mt: 2 }}>Rubric Hints</Typography>
            {form.rubricHints.map((r, i) => (
              <TextField key={`h-${i}`} value={r || ''} onChange={(e) => setHint(i, e.target.value)} fullWidth sx={{ mb: 1 }} />
            ))}
            <Button size="small" onClick={addHint}>+ Add Hint</Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
